import { useCallback, useMemo } from 'react';
import { useTranslation } from '@aimsos/i18n';
import {
  FilterSlideout,
  createMultiSelectSection,
  createSingleSelectSection,
} from '@aimsos/ui';
import { getShellMainContentEl } from '../../../shared/utils/shellPortal.js';

/** Deselecting the "any" row means "no filter" — the same sentinel the page uses. */
const ANY_VALUE = '';

/**
 * The "All filters" panel.
 *
 * Not optional decoration: `@aimsos/ui`'s `Filters` bar shows **zero** chips
 * below 1280px (see `filtersResponsive.js` — "en pantallas medianas y pequeñas
 * no se muestran slots en barra; todo va al slideout") so without this panel the
 * four dimensions are unreachable on any narrower screen, which is most laptops
 * once the shell's sidebar is open.
 *
 * **Staged.** Every section writes to `draft`; only `Apply` commits it, so
 * dismissing the panel leaves the list untouched and it never twitches while
 * someone is still choosing.
 *
 * Props are documented here rather than with `prop-types`: no other component
 * in this app imports that package and `apps/agents` does not depend on it, so
 * the import resolved in dev through hoisting and broke the production build.
 *
 * @param {object} props
 * @param {boolean} [props.isOpen]
 * @param {() => void} [props.onClose]
 * @param {{ status?: string[], type?: string[], category?: string[], createdWithin?: string }} props.draft
 * @param {(field: string, value: unknown) => void} props.setDraftField
 * @param {() => void} props.applyDraft
 * @param {() => void} props.clearDraft
 */
function AgentsFiltersSlideout({ isOpen, onClose, draft, setDraftField, applyDraft, clearDraft }) {
  const { t } = useTranslation('agents');

  // Into the shell's content column, never `body`. A body portal makes the
  // backdrop `fixed inset-0`, dimming the shell's top bar and module nav —
  // saying the whole product is blocked when only this list is. The other
  // agents drawers already resolve their target this way; `Drawer` switches
  // its own layers to `absolute`/`h-full` as soon as the target is not `body`.
  const portalTarget = useMemo(() => (isOpen ? getShellMainContentEl() : null), [isOpen]);

  const sections = useMemo(
    () => [
      createMultiSelectSection({
        id: 'status',
        title: t('list.filters.status'),
        options: [
          { value: 'active', label: t('list.filterOptions.status.active') },
          { value: 'disabled', label: t('list.filterOptions.status.disabled') },
          { value: 'draft', label: t('list.filterOptions.status.draft') },
          { value: 'archived', label: t('list.filterOptions.status.archived') },
        ],
        value: draft.status ?? [],
        onChange: (next) => setDraftField('status', next ?? []),
      }),
      createMultiSelectSection({
        id: 'type',
        title: t('list.filters.type'),
        options: [{ value: 'ai', label: t('list.filterOptions.type.ai') }],
        value: draft.type ?? [],
        onChange: (next) => setDraftField('type', next ?? []),
      }),
      createMultiSelectSection({
        id: 'category',
        title: t('list.filters.category'),
        options: [{ value: 'Service', label: t('list.filterOptions.category.Service') }],
        value: draft.category ?? [],
        onChange: (next) => setDraftField('category', next ?? []),
      }),
      createSingleSelectSection({
        // "Created", not "Last update" (CT-5291): the list endpoint only filters
        // on `created_at`.
        id: 'createdWithin',
        title: t('list.filters.createdWithin'),
        allValue: ANY_VALUE,
        options: [
          { value: ANY_VALUE, label: t('list.filters.createdWithinOptions.any') },
          { value: 'today', label: t('list.filters.createdWithinOptions.today') },
          { value: 'last7days', label: t('list.filters.createdWithinOptions.last7days') },
          { value: 'last30days', label: t('list.filters.createdWithinOptions.last30days') },
          { value: 'last90days', label: t('list.filters.createdWithinOptions.last90days') },
        ],
        value: draft.createdWithin ?? ANY_VALUE,
        onChange: (next) => setDraftField('createdWithin', next ?? ANY_VALUE),
      }),
    ],
    [draft.status, draft.type, draft.category, draft.createdWithin, setDraftField, t],
  );

  // `FilterSlideout`'s own footer copy defaults to English — pass it through
  // or "Clear all" / "Apply filters" ship untranslated inside a Spanish panel.
  const slideoutI18n = useMemo(
    () => ({
      close: t('list.filters.close'),
      clearAll: t('list.filters.clearAll'),
      apply: t('list.filters.apply'),
    }),
    [t],
  );

  const handleApply = useCallback(() => {
    applyDraft();
    onClose?.();
  }, [applyDraft, onClose]);

  return (
    <FilterSlideout
      isOpen={isOpen}
      onClose={onClose}
      title={t('list.filters.allFilters')}
      sections={sections}
      onClearAll={clearDraft}
      onApply={handleApply}
      portalTarget={portalTarget ?? undefined}
      i18n={slideoutI18n}
    />
  );
}

export default AgentsFiltersSlideout;
